import React, { useState, useRef, useEffect } from "react";
import Select, { SingleValue } from "react-select";
import {
  CanvasIconBlack,
  CaretDownSolidIcon,
  CaretUpSolidIcon,
  DeleteIcon,
  EditIcon,
  FilterIcon,
  ResetIcon,
  TickIcon,
} from "../../../assets/icons";
import Pagination from "../../shared/common/pagination";
import SelectPageSize from "../../shared/common/pageSizeDropdown";
import SchoolDetail from "./schoolDetail";
import { getStateAll } from "../../../api/subject.service";
import {
  deleteDistrictById,
  getMiniCRMDistrictOptions,
  getMiniCRMDistricts,
} from "../../../api/miniCrm.service";
import { IReactSelect } from "../../../model/interface/keyValuePair";
import { IDistrictOption } from "../../../model/interface/districts";
import { formatDistrictOption } from "../../../utils/districtOptionHelper";
import { XIcon } from "@heroicons/react/outline";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import Loader from "../../shared/loader/loader";
import { useNavigate } from "react-router";
import constant from "../../../utils/constant";
import AllowedTo from "../../shared/common/rbac";
import Permissions from "../../../../src/utils/permissions";
import { CanvasAccountSettings } from "./CanvasAccountSettings";

const Districts: React.FC = (props: any) => {
  const defaultSelect = { label: "", value: 0 };
  const paginationRef: any = useRef(undefined);
  const pageSizeRef: any = useRef(undefined);
  const [pageNumber, setPageNumber] = useState<any>(1);
  const [sortCol, setSortCol] = useState<string>("");
  const [sortByDesc, setSortByDesc] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [states, setStates] = useState<Array<any>>([]);
  const [selectedState, setSelectedStates] = useState<IReactSelect>();
  const [districts, setDistricts] = useState<Array<IDistrictOption>>([]);
  const [districtsLoading, setDistrictsLoading] = useState<boolean>(false);
  const [selectedDistrict, setSelectedDistrict] =
    useState<IReactSelect>(defaultSelect);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [districtResults, setDistrictResults] = useState<Array<any>>([]);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedRegion, setSelectedRegion] = useState<any>({
    label: "",
    value: "",
  });
  const [district, setDistrict] = useState<any>({});
  const [city, setCity] = useState<string>("");
  const [editFields, setEditFields] = useState<any>();
  const [deleteDistrict, setDeleteDistrict] = useState<any>();
  const [editSelectedDistrict, setEditSelectedDistrict] =
    useState<IReactSelect>(defaultSelect);
  const [loading, setLoading] = useState<boolean>(false);
  const [isFilterJustLoaded, setIsFilterJustLoaded] = useState<boolean>(false);
  const navigate = useNavigate();

  const saveFilterState = (isReset?) => {
    if (isReset) {
      localStorage.removeItem("miniCRM_districtFields");
    } else {
      const districtFields = {
        selectedState: selectedState,
        selectedDistrict: selectedDistrict,
        city: city,
      };
      localStorage.setItem(
        "miniCRM_districtFields",
        JSON.stringify(districtFields)
      );
    }
  };

  const loadFilterState = () => {
    const savedFields = localStorage.getItem("miniCRM_districtFields");
    if (savedFields) {
      const districtFields = JSON.parse(savedFields);
      setSelectedStates(districtFields.selectedState);
      setSelectedDistrict(districtFields.selectedDistrict);
      setCity(districtFields.city);
      setIsFilterJustLoaded(true);
      return districtFields.selectedState?.value ?? 0;
    }
    return 0;
  };

  const loadDistricts = async (stateId = 0) => {
    setDistrictsLoading(true);
    try {
      const options = await getMiniCRMDistrictOptions(stateId);
      setDistricts(options);
      return options;
    } finally {
      setDistrictsLoading(false);
    }
  };

  useEffect(() => {
    loadDistricts(loadFilterState());
  }, []);

  useEffect(() => {
    if (isFilterJustLoaded) {
      getDistricts();
      setIsFilterJustLoaded(false);
    }
  }, [isFilterJustLoaded]);

  useEffect(() => {
    if (isLoaded) {
      getDistricts();
    }
  }, [pageNumber]);

  useEffect(() => {
    getStates();
    if (pageNumber === 1 && isLoaded) {
      getDistricts();
      setIsLoaded(true);
    } else {
      paginationRef?.current?.onReset();
    }
  }, [pageSize]);

  useEffect(() => {
    if (isLoaded) {
      getDistricts();
    }
  }, [sortByDesc, sortCol]);

  const getStates = async () => {
    const resp: any = await getStateAll(9999, 1);
    if (resp && resp.data) {
      setStates(
        resp?.data?.data?.map((ds) => ({
          label: ds.name,
          value: ds.stateId,
        }))
      );
    }
  };

  const getDistricts = async (isReset?) => {
    setLoading(true);
    let params = {};
    if (isReset) {
      params = {
        stateId: 0,
        region: "",
        districtId: 0,
        districtName: "",
        city: "",
        orderByColumn: sortCol,
        sortDir: sortByDesc ? "desc" : "asc",
        pageSize: 10,
        pageNumber: 1,
      };
    } else {
      params = {
        stateId: selectedState?.value,
        region: selectedRegion?.value,
        districtId: selectedDistrict?.value,
        districtName: selectedDistrict?.label,
        city,
        orderByColumn: sortCol,
        sortDir: sortByDesc ? "desc" : "asc",
        pageSize: pageSize,
        pageNumber: pageNumber,
      };
    }

    saveFilterState(isReset);

    const resp = await getMiniCRMDistricts(params);
    setLoading(false);
    if (resp && resp.data && resp.data.data) {
      setDistrictResults(resp.data.data);
      setTotalPages(
        Math.ceil(resp.data.totalRecords / (isReset ? 10 : pageSize))
      );
      setTotalRecords(resp.data.totalRecords);
      setIsLoaded(true);
    }
  };

  const handleSortBy = (column: string) => {
    if (sortCol === column) {
      setSortByDesc(!sortByDesc);
    } else {
      setSortByDesc(false);
      setSortCol(column);
    }
  };

  const handleModal = () => {
    setModalOpen(!modalOpen);
  };

  const onStateChange = async (e) => {
    setSelectedStates(e);
    const options = await loadDistricts(e.value);
    if (
      selectedDistrict?.value &&
      !options.some((ds) => ds.value === selectedDistrict.value)
    ) {
      setSelectedDistrict(defaultSelect);
    }
  };

  const resetClickHandler = () => {
    if (selectedState?.value) loadDistricts();
    setSelectedStates(defaultSelect);
    setSelectedRegion({ label: "", value: "" });
    setSelectedDistrict(defaultSelect);
    setDistrict({});
    getDistricts(true);
    paginationRef?.current?.onReset();
    pageSizeRef?.current?.onReset();
    const form: any = document.getElementById("users_form");
    if (form) form.reset();
  };

  const editModal = (district) => {
    setEditSelectedDistrict({
      label: district.name,
      value: district.districtId,
    });
    setDistrict(district);
  };

  const onDelete = async () => {
    setLoading(true);
    setDeleteDistrict({
      open: false,
      districtId: 0,
      districtName: "",
    });
    try {
      const resp = await deleteDistrictById({
        districtId: deleteDistrict?.districtId,
        updatedByUser: props?.profile?.userId,
      });
      setLoading(false);
      if (resp.data > 0) {
        toast.success("District deleted successfully");
        getDistricts();
      } else {
        toast.error("District delete failed");
      }
    } catch (e) {
      toast.error("District delete failed");
    }
  };

  const openDistrict = (districtId) => {
    navigate(constant.DISTRICTDETAIL.replace(":id", districtId));
  };

  const onKeyPress = (e) => {
    if (e.charCode === 13) getDistricts();
  };

  const handleSelectedDistrict = (option: SingleValue<IReactSelect> | null) => {
    if (option) {
      setSelectedDistrict(option);
    }
  };

  return (
    <form id="users_form" className="p-3" onKeyPress={onKeyPress}>
      {loading && <Loader />}

      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-3">
          <label className="text-sm text-gray-700">State</label>
          <Select
            className="mt-1 text-sm"
            value={selectedState}
            options={states}
            onChange={onStateChange}
            theme={(theme) => ({
              ...theme,
              colors: {
                ...theme.colors,
                primary25: "#B3B3B3",
                primary: "#041E42",
              },
            })}
            styles={{
              control: (base, state) => ({
                ...base,
                background:
                  document.getElementsByClassName("dark")?.length > 0
                    ? "#1A1C23"
                    : "white",
                borderColor:
                  document.getElementsByClassName("dark")?.length > 0
                    ? "#6B7280"
                    : "#D1D5DB",
                borderWidth: "1px",
                "&:hover": {
                  borderColor:
                    state.isFocused &&
                    document.getElementsByClassName("dark")?.length > 0
                      ? "#6b7280"
                      : state.isFocused
                      ? "#4c5563"
                      : "",
                },
              }),
              singleValue: (base) => ({
                ...base,
                color:
                  document.getElementsByClassName("dark")?.length > 0
                    ? "white"
                    : "#374051",
              }),
              input: (base) => ({
                ...base,
                color:
                  document.getElementsByClassName("dark")?.length > 0
                    ? "white"
                    : "",
              }),
            }}
          />
        </div>

        <div className="col-span-3">
          <label className="text-sm text-gray-700">District</label>
          <Select
            className="mt-1 text-sm"
            value={selectedDistrict}
            options={districts}
            onChange={handleSelectedDistrict}
            isLoading={districtsLoading}
            formatOptionLabel={(option, { context }) =>
              formatDistrictOption(option, context, selectedState?.value)
            }
            theme={(theme) => ({
              ...theme,
              colors: {
                ...theme.colors,
                primary25: "#B3B3B3",
                primary: "#041E42",
              },
            })}
            styles={{
              control: (base, state) => ({
                ...base,
                background:
                  document.getElementsByClassName("dark")?.length > 0
                    ? "#1A1C23"
                    : "white",
                borderColor:
                  document.getElementsByClassName("dark")?.length > 0
                    ? "#6B7280"
                    : "#D1D5DB",
                borderWidth: "1px",
                "&:hover": {
                  borderColor:
                    state.isFocused &&
                    document.getElementsByClassName("dark")?.length > 0
                      ? "#6b7280"
                      : state.isFocused
                      ? "#4c5563"
                      : "",
                },
              }),
              singleValue: (base) => ({
                ...base,
                color:
                  document.getElementsByClassName("dark")?.length > 0
                    ? "white"
                    : "#374051",
              }),
              input: (base) => ({
                ...base,
                color:
                  document.getElementsByClassName("dark")?.length > 0
                    ? "white"
                    : "",
              }),
            }}
          />
        </div>

        <div className="col-span-3">
          <label className="text-sm text-gray-700">City</label>
          <input
            value={city}
            onChange={(e) => setCity(e.target.value)}
            name="city"
            type="text"
            autoComplete="off"
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
          />
        </div>
      </div>

      <div className="flex justify-start mb-5">
        <button
          autoFocus
          type="button"
          onClick={() => getDistricts()}
          className="mt-4 inline-flex items-center text-sm text-white dark:text-white gap-2 bg-primary-color hover:bg-secondary-color py-1.5 px-4 rounded-lg"
        >
          <FilterIcon className="w-3 h-3" />
          <span>Filter</span>
        </button>
        <button
          type="button"
          onClick={resetClickHandler}
          className="mt-4 py-1.5 px-4 inline-flex ml-3 items-center text-sm rounded-lg text-gray-700 text-primary-color border border-gray-300 gap-2 bg-white hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color "
        >
          <ResetIcon className="w-3 h-3" />
          <span>Reset Filter</span>
        </button>
      </div>

      <div id="dataContainer" className="mt-4 border-t pt-6">
        <div className="flex flex-col mt-1">
          <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
              <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-primary-color">
                    <tr>
                      <th
                        scope="col"
                        className="px-3 py-2 font-light text-sm text-white dark:text-white tracking-wider cursor-pointer"
                        onClick={() => handleSortBy("name")}
                      >
                        <span className="flex">
                          District
                          {sortCol === "name" && (
                            <span>
                              {!sortByDesc ? (
                                <CaretUpSolidIcon className="w-5 h-5"></CaretUpSolidIcon>
                              ) : (
                                <CaretDownSolidIcon className="w-5 h-5"></CaretDownSolidIcon>
                              )}
                            </span>
                          )}
                        </span>
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-2 font-light text-sm text-white dark:text-white tracking-wider cursor-pointer"
                        onClick={() => handleSortBy("accountLoginName")}
                      >
                        <span className="flex">
                          Account Login Name
                          {sortCol === "accountLoginName" && (
                            <span>
                              {!sortByDesc ? (
                                <CaretUpSolidIcon className="w-5 h-5"></CaretUpSolidIcon>
                              ) : (
                                <CaretDownSolidIcon className="w-5 h-5"></CaretDownSolidIcon>
                              )}
                            </span>
                          )}
                        </span>
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-2 font-light text-sm text-white dark:text-white tracking-wider cursor-pointer"
                        onClick={() => handleSortBy("city")}
                      >
                        <span className="flex">
                          City
                          {sortCol === "city" && (
                            <span className="">
                              {!sortByDesc ? (
                                <CaretUpSolidIcon className="w-5 h-5"></CaretUpSolidIcon>
                              ) : (
                                <CaretDownSolidIcon className="w-5 h-5"></CaretDownSolidIcon>
                              )}
                            </span>
                          )}
                        </span>
                      </th>

                      <th
                        scope="col"
                        className="px-3 py-2 font-light text-sm text-white dark:text-white tracking-wider cursor-pointer"
                        onClick={() => handleSortBy("stateName")}
                      >
                        <span className="flex">
                          State
                          {sortCol === "stateName" && (
                            <span>
                              {!sortByDesc ? (
                                <CaretUpSolidIcon className="w-5 h-5"></CaretUpSolidIcon>
                              ) : (
                                <CaretDownSolidIcon className="w-5 h-5"></CaretDownSolidIcon>
                              )}
                            </span>
                          )}
                        </span>
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-2 font-light text-sm text-white dark:text-white tracking-wider"
                      >
                        Teacher Count
                      </th>

                      <th
                        scope="col"
                        className="px-3 py-2 font-light text-sm text-white dark:text-white tracking-wider"
                      >
                        Student Count
                      </th>

                      <th
                        scope="col"
                        className="px-3 py-2 font-light text-sm text-white dark:text-white tracking-wider"
                      >
                        NWEA Id
                      </th>

                      <th
                        scope="col"
                        className="px-3 py-2 font-light text-sm text-white dark:text-white tracking-wider"
                      >
                        Clever
                      </th>

                      <th
                        scope="col"
                        className="px-3 py-2 font-light text-sm text-center text-white dark:text-white tracking-wider"
                      >
                        Canvas
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-2 font-light text-sm text-center text-white dark:text-white tracking-wider"
                      >
                        ClassLink ID
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-2 font-light text-sm text-center text-white dark:text-white tracking-wider"
                      >
                        Roster Active
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-2 font-light text-sm text-center text-white dark:text-white tracking-wider"
                      >
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {districtResults?.map((district, i) => (
                      <tr
                        key={i}
                        className={`align-top ${
                          i % 2 === 0 ? "bg-white " : "bg-gray-100"
                        }`}
                      >
                        <td className=" px-3 py-2  text-sm text-left font-normal relative">
                          <button
                            className="text-indigo-500"
                            type="button"
                            onClick={() => {
                              openDistrict(district.districtId);
                            }}
                          >
                            {district.name}
                          </button>{" "}
                        </td>
                        <td className=" px-3 py-2  text-sm text-left font-normal relative">
                          {district.accountLoginName}
                        </td>
                        <td className=" px-3 py-2  text-sm text-left font-normal relative">
                          {district.city}
                        </td>
                        <td className=" px-3 py-2  text-sm text-left font-normal relative">
                          {district.stateName}
                        </td>
                        <td className=" px-3 py-2  text-sm text-left font-normal relative">
                          {district.teacherCount}
                        </td>
                        <td className=" px-3 py-2  text-sm text-left font-normal relative">
                          {district.studentCount}
                        </td>
                        <td className=" px-3 py-2  text-sm text-left font-normal relative">
                          {district.nweaId !==
                          "00000000-0000-0000-0000-000000000000"
                            ? district.nweaId
                            : ""}
                        </td>
                        <td className=" px-3 py-2  text-sm text-left font-normal relative">
                          {district.cleverId}
                        </td>
                        <td className=" px-3 py-2  text-sm text-left font-normal relative">
                          {district.canvasAccountId}
                        </td>
                        <td className=" px-3 py-2  text-sm text-left font-normal relative">
                          {district.classlinkId}
                        </td>
                        <td className=" px-3 py-2  text-sm text-left font-normal relative">
                          <div className="flex justify-center items-center">
                            {district.isRosterActive && <TickIcon></TickIcon>}
                          </div>
                        </td>
                        <td className=" px-3 py-2  text-sm text-left font-normal relative">
                          <div className="flex justify-center items-center">
                            <AllowedTo perform={Permissions.district_modify}>
                              <CanvasIconBlack
                                title="Canvas Account Settings"
                                className="mr-3 h-5 w-5 text-xs font-light cursor-pointer hover:text-secondary-color transaction ease-in-out duration-300"
                                onClick={() => {
                                  setEditFields({ open: true });
                                  editModal(district);
                                }}
                              />{" "}
                              <EditIcon
                                title="Edit District"
                                onClick={() => {
                                  openDistrict(district.districtId);
                                }}
                                className="mr-3 h-5 w-5 text-xs font-light cursor-pointer hover:text-secondary-color transaction ease-in-out duration-300"
                              />
                            </AllowedTo>
                            <AllowedTo perform={Permissions.district_delete}>
                              {" "}
                              <DeleteIcon
                                onClick={() =>
                                  setDeleteDistrict({
                                    open: true,
                                    districtId: district.districtId,
                                    districtName: district.name,
                                  })
                                }
                                className="mr-3 h-5 w-5 text-xs font-light cursor-pointer hover:text-red-500 transaction ease-in-out duration-300"
                              />
                            </AllowedTo>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {districtResults.length === 0 && (
                  <p className="p-2">No Records Found</p>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-between items-center">
            <Pagination
              pages={totalPages}
              setPageNumber={setPageNumber}
              ref={paginationRef}
            />
            <span>
              <span className="text-sm pr-4 ml-auto text-left-700">
                Showing {pageNumber} to{" "}
                {totalRecords < pageSize ? totalRecords : pageSize} of{" "}
                {totalRecords} results{" "}
              </span>

              <SelectPageSize
                setPageSize={setPageSize}
                type={"second"}
                ref={pageSizeRef}
              />
            </span>
          </div>
        </div>
      </div>

      {modalOpen && <SchoolDetail handleModal={handleModal} />}

      {editFields?.open && (
        <CanvasAccountSettings
          districtId={editSelectedDistrict.value}
          onClose={() => setEditFields({ open: false })}
        />
      )}

      {deleteDistrict?.open && (
        <div
          className="fixed z-10 inset-0 overflow-y-auto"
          aria-labelledby="modal-title"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              aria-hidden="true"
            ></div>

            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>

            <div className="rounded-lg inline-block align-bottom bg-white px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6 h-auto">
              <div className="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
                <button
                  onClick={() =>
                    setDeleteDistrict({
                      open: false,
                      districtName: "",
                      districtId: 0,
                    })
                  }
                  type="button"
                  className="bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-text-primary"
                >
                  <XIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>

              <div className="flex justify-center font-semibold">
                <h2 className="text-gray-700  text-2xl">Are you sure?</h2>
              </div>

              <div className="mt-2 flex justify-center">
                <h2 className="text-gray-700 text-lg">
                  You want to delete{" "}
                  <span className="font-semibold text-black text-xl">
                    {deleteDistrict.districtName}
                  </span>
                </h2>
              </div>

              <div className="flex justify-end pt-4">
                <button
                  type="button"
                  onClick={() => onDelete()}
                  className="mt-4 inline-flex items-center text-sm text-white dark:text-white gap-2 bg-primary-color hover:bg-secondary-color py-1.5 px-4 rounded-lg"
                >
                  <span>Delete</span>
                </button>

                <button
                  type="button"
                  onClick={() =>
                    setDeleteDistrict({
                      open: false,
                      districtId: 0,
                      districtName: "",
                    })
                  }
                  className="mt-4 py-1.5 px-4 inline-flex ml-3 items-center text-sm rounded-lg text-gray-700 text-primary-color border border-gray-300 gap-2 bg-white hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color "
                >
                  <span>Cancel</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </form>
  );
};

const mapStateToProps = (state: any) => {
  return {
    profile: state.profile,
  };
};
export default connect(mapStateToProps, {})(Districts);
